"use client";

/**
 * Interaction primitives for composed whiteboard exercises.
 *
 * Blocks report summary events for meaningful checks/commits and, for a few
 * construction tasks, bounded in-progress "working" state so the tutor can
 * see learner reasoning without receiving tap noise. All interactions are
 * tap-based to stay mobile-friendly.
 */
import type { WhiteboardRenderState } from "@/lib/whiteboard/boardState";
import type {
  BucketSortBlockSchema,
  ContrastPairBlockSchema,
  DiscourseFunctionBlockSchema,
  HighlightTargetBlockSchema,
  ListeningDiscriminationBlockSchema,
  MorphologyGridBlockSchema,
  OrderingBlockSchema,
  PairMatchBlockSchema,
  PhraseBankRoleplayBlockSchema,
  PronunciationFocusBlockSchema,
  RevealBlockSchema,
  SentenceRepairBlockSchema,
  SliderBlockSchema,
  SlotSentenceBlockSchema,
  TapGroupBlockSchema,
  TextBlockSchema,
  TimelineBuilderBlockSchema,
  TransformationFrameBlockSchema,
  TranslationChoiceBlockSchema,
} from "@/lib/whiteboard/spec";
import { normalizeAnswer } from "@/lib/whiteboard/spec";
import { motion } from "framer-motion";
import { Check, Ear, Eye, Mic2, RotateCcw, Table2 } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import type { z } from "zod";
import { WhiteboardWriteInText } from "./WhiteboardWriteInText";
import {
  type WhiteboardCardEvent,
  composedBlockTargetId,
  getWhiteboardMarkClass,
  getWhiteboardTargetMark,
  isWhiteboardTargetHighlighted,
} from "./types";

/** Block-level event, before the parent attaches the block label. */
export type BlockEvent = Omit<WhiteboardCardEvent, "event" | "block"> & {
  event?: WhiteboardCardEvent["event"];
};
type OnBlockEvent = (event: BlockEvent) => void;
type RenderAwareBlockProps = {
  renderState?: WhiteboardRenderState;
  blockIndex?: number;
};

const chipBase =
  "rounded-lg border px-3 py-1.5 text-base font-semibold transition-colors duration-150";
const chipIdle =
  "border-white/15 bg-white/[0.06] text-[#F9F9F9] hover:border-[#FD954F]/60 hover:bg-[#FD954F]/10";
const chipActive = "border-[#FD954F]/60 bg-[#FD954F]/15 text-[#FFD9B8]";
const chipCorrect = "border-[#22C55E]/60 bg-[#22C55E]/20 text-[#4ADE80]";
const chipWrong = "border-[#EF4444]/40 bg-[#EF4444]/10 text-[#FCA5A5]";

function getTutorTargetClass(
  value: string,
  renderState?: WhiteboardRenderState,
  targetId?: string
): string {
  if (!renderState) return "";
  const highlightClass = isWhiteboardTargetHighlighted(value, renderState, targetId)
    ? "whiteboard-tutor-highlight-pulse border-[#FD954F]/80 bg-[#FD954F]/20 text-[#FFD9B8]"
    : "";
  return `${highlightClass} ${getWhiteboardMarkClass(
    getWhiteboardTargetMark(value, renderState, targetId)
  )}`;
}

function getTutorTargetTitle(
  value: string,
  renderState?: WhiteboardRenderState,
  targetId?: string
): string | undefined {
  return renderState ? getWhiteboardTargetMark(value, renderState, targetId)?.label : undefined;
}

const CheckButton: React.FC<{ disabled?: boolean; onClick: () => void }> = ({
  disabled,
  onClick,
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="flex items-center gap-1.5 rounded-xl bg-[#FD954F] px-3.5 py-2 text-sm font-bold text-white transition-colors hover:bg-[#F28747] disabled:bg-[#3A3A3C] disabled:text-[#8E8E93]"
  >
    <Check size={15} />
    Check
  </button>
);

const BlockLabel: React.FC<{ label?: string }> = ({ label }) =>
  label ? (
    <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#AEAEB2]">{label}</p>
  ) : null;

function shuffled<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

// ─── text ────────────────────────────────────────────────────────────

export const TextBlock: React.FC<{ block: z.infer<typeof TextBlockSchema> }> = ({ block }) => (
  <WhiteboardWriteInText
    as="p"
    text={block.content}
    className={
      block.style === "muted"
        ? "text-sm italic text-[#AEAEB2]"
        : block.style === "emphasis"
          ? "text-lg sm:text-xl font-semibold text-[#F9F9F9]"
          : "text-base text-[#F9F9F9]"
    }
  />
);

// ─── tap_group ───────────────────────────────────────────────────────

export const TapGroupBlock: React.FC<
  {
    block: z.infer<typeof TapGroupBlockSchema>;
    onEvent: OnBlockEvent;
  } & RenderAwareBlockProps
> = ({ block, renderState, blockIndex = 0, onEvent }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [done, setDone] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const hasCorrect = Boolean(block.correct?.length);
  const correctSet = useMemo(
    () => new Set((block.correct ?? []).map(normalizeAnswer)),
    [block.correct]
  );

  const finish = (picks: Set<string>) => {
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    const pickedValues = [...picks];
    const pickedNormalized = new Set([...picks].map(normalizeAnswer));
    const correct = hasCorrect
      ? pickedNormalized.size === correctSet.size &&
        [...pickedNormalized].every((p) => correctSet.has(p))
      : undefined;
    const singlePickIndex =
      pickedValues.length === 1
        ? block.options.findIndex((option) => option === pickedValues[0])
        : -1;
    if (correct !== false) setDone(true);
    onEvent({
      response: pickedValues.join(", "),
      action: hasCorrect ? "selected_option" : "selected_choice",
      target: block.label,
      targetId:
        singlePickIndex >= 0
          ? composedBlockTargetId(blockIndex, "option", singlePickIndex)
          : undefined,
      correct,
      expected: block.correct?.join(", "),
      resolved: hasCorrect ? correct === true : true,
      attempt: nextAttempt,
    });
  };

  const handleTap = (option: string) => {
    if (done) return;
    if (block.multiple) {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(option)) next.delete(option);
        else next.add(option);
        return next;
      });
    } else {
      setSelected(new Set([option]));
      finish(new Set([option]));
    }
  };

  return (
    <div>
      <BlockLabel label={block.label} />
      <div className="flex flex-wrap gap-2">
        {block.options.map((option, index) => {
          const optionTargetId = composedBlockTargetId(blockIndex, "option", index);
          const isPicked = selected.has(option);
          const isCorrectPick = done && hasCorrect && correctSet.has(normalizeAnswer(option));
          const tutorClass = getTutorTargetClass(option, renderState, optionTargetId);
          return (
            <motion.button
              key={option}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleTap(option)}
              disabled={done}
              data-whiteboard-target-id={optionTargetId}
              className={`${chipBase} ${
                isCorrectPick && isPicked
                  ? chipCorrect
                  : done && isPicked && hasCorrect
                    ? chipWrong
                    : isPicked
                      ? chipActive
                      : chipIdle
              } ${tutorClass} ${done && !isPicked ? "opacity-40" : ""}`}
              title={getTutorTargetTitle(option, renderState, optionTargetId)}
            >
              {option}
            </motion.button>
          );
        })}
      </div>
      {block.multiple && !done && (
        <div className="mt-3">
          <CheckButton disabled={selected.size === 0} onClick={() => finish(selected)} />
        </div>
      )}
    </div>
  );
};

// ─── slot_sentence ───────────────────────────────────────────────────

export const SlotSentenceBlock: React.FC<
  {
    block: z.infer<typeof SlotSentenceBlockSchema>;
    onEvent: OnBlockEvent;
  } & RenderAwareBlockProps
> = ({ block, renderState, blockIndex = 0, onEvent }) => {
  const parts = block.sentence.split(/_{3,}/);
  const gapCount = Math.max(parts.length - 1, 1);
  const [fills, setFills] = useState<(string | null)[]>(Array(gapCount).fill(null));
  const [done, setDone] = useState(false);
  const [lastWrong, setLastWrong] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const usedCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const fill of fills) {
      if (fill) counts.set(fill, (counts.get(fill) ?? 0) + 1);
    }
    return counts;
  }, [fills]);

  const poolAvailability = (word: string) => {
    const total = block.pool.filter((p) => p === word).length;
    return total - (usedCounts.get(word) ?? 0);
  };

  const reportWorkingState = (
    nextFills: (string | null)[],
    action: "filled_gap" | "cleared_gap",
    targetId?: string
  ) => {
    const filledCount = nextFills.filter((fill) => fill !== null).length;
    onEvent({
      event: "working",
      response: nextFills.map((fill) => fill ?? "_").join(" / "),
      action,
      target: block.sentence,
      targetId,
      expected: block.answers.join(" / "),
      progress: `${filledCount}/${gapCount} gaps filled`,
      resolved: false,
    });
  };

  const placeWord = (word: string) => {
    if (done) return;
    setLastWrong(false);
    setFills((prev) => {
      const next = [...prev];
      const emptyIndex = next.indexOf(null);
      if (emptyIndex !== -1) {
        next[emptyIndex] = word;
        reportWorkingState(
          next,
          "filled_gap",
          composedBlockTargetId(blockIndex, "gap", emptyIndex)
        );
      }
      return next;
    });
  };

  const clearGap = (index: number) => {
    if (done) return;
    setLastWrong(false);
    setFills((prev) => {
      const next = [...prev];
      next[index] = null;
      reportWorkingState(next, "cleared_gap", composedBlockTargetId(blockIndex, "gap", index));
      return next;
    });
  };

  const allFilled = fills.every((fill) => fill !== null);

  const handleCheck = () => {
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    const correct = fills.every(
      (fill, index) =>
        fill !== null && normalizeAnswer(fill) === normalizeAnswer(block.answers[index] ?? "")
    );
    setDone(correct);
    setLastWrong(!correct);
    onEvent({
      response: fills.map((f) => f ?? "_").join(" / "),
      correct,
      expected: block.answers.join(" / "),
      attempt: nextAttempt,
    });
  };

  return (
    <div>
      <BlockLabel label={block.label} />
      <p className="text-lg leading-relaxed font-medium text-[#F9F9F9]">
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            <WhiteboardWriteInText text={part} inline initialDelayMs={index * 280} />
            {index < parts.length - 1 &&
              (fills[index] ? (
                <button
                  data-whiteboard-target-id={composedBlockTargetId(blockIndex, "gap", index)}
                  onClick={() => clearGap(index)}
                  disabled={done}
                  className={`mx-1 inline-block rounded-md border px-2 py-0.5 font-bold ${
                    done
                      ? "border-[#22C55E]/50 bg-[#22C55E]/15 text-[#4ADE80]"
                      : lastWrong
                        ? "border-[#EF4444]/50 bg-[#EF4444]/10 text-[#FCA5A5]"
                        : "border-[#FD954F]/40 bg-[#FD954F]/10 text-[#FFD9B8]"
                  }`}
                >
                  {fills[index]}
                </button>
              ) : (
                <span className="mx-1 inline-block w-16 translate-y-1 border-b-2 border-dashed border-[#FD954F]/70" />
              ))}
          </React.Fragment>
        ))}
      </p>
      {!done && (
        <>
          <div className="mt-3 flex flex-wrap gap-2">
            {[...new Set(block.pool)].map((word, index) => {
              const targetId = composedBlockTargetId(blockIndex, "pool", index);
              return (
                <motion.button
                  key={word}
                  whileTap={{ scale: 0.94 }}
                  data-whiteboard-target-id={targetId}
                  onClick={() => placeWord(word)}
                  disabled={poolAvailability(word) <= 0 || allFilled}
                  className={`${chipBase} ${chipIdle} ${getTutorTargetClass(
                    word,
                    renderState,
                    targetId
                  )} disabled:opacity-30`}
                  title={getTutorTargetTitle(word, renderState, targetId)}
                >
                  {word}
                </motion.button>
              );
            })}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <CheckButton disabled={!allFilled} onClick={handleCheck} />
            {lastWrong && (
              <span className="text-sm font-medium text-[#FCA5A5]">
                Not quite — tap a filled gap to change it.
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ─── bucket_sort ─────────────────────────────────────────────────────

export const BucketSortBlock: React.FC<
  {
    block: z.infer<typeof BucketSortBlockSchema>;
    onEvent: OnBlockEvent;
  } & RenderAwareBlockProps
> = ({ block, renderState, blockIndex = 0, onEvent }) => {
  const items = useMemo(() => shuffled(block.items.map((item, id) => ({ ...item, id }))), [block]);
  const [placements, setPlacements] = useState<Map<number, string>>(new Map());
  const [activeItem, setActiveItem] = useState<number | null>(null);
  const [done, setDone] = useState(false);
  const [lastWrong, setLastWrong] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const unplaced = items.filter((item) => !placements.has(item.id));
  const allPlaced = unplaced.length === 0;

  const summarizePlacements = (nextPlacements: Map<number, string>) =>
    items
      .filter((item) => nextPlacements.has(item.id))
      .map((item) => `${item.text} -> ${nextPlacements.get(item.id)}`)
      .join(", ") || "(empty)";

  const reportWorkingState = (
    nextPlacements: Map<number, string>,
    action: "placed_bucket" | "removed_bucket",
    targetId?: string
  ) => {
    onEvent({
      event: "working",
      response: summarizePlacements(nextPlacements),
      action,
      target: block.label,
      targetId,
      expected: items.map((item) => `${item.text} -> ${item.bucket}`).join(", "),
      progress: `${nextPlacements.size}/${items.length} items sorted`,
      resolved: false,
    });
  };

  const placeActive = (bucket: string) => {
    if (activeItem === null || done) return;
    setLastWrong(false);
    setPlacements((prev) => {
      const next = new Map(prev).set(activeItem, bucket);
      reportWorkingState(
        next,
        "placed_bucket",
        composedBlockTargetId(blockIndex, "item", activeItem)
      );
      return next;
    });
    setActiveItem(null);
  };

  const unplace = (id: number) => {
    if (done) return;
    setLastWrong(false);
    setPlacements((prev) => {
      const next = new Map(prev);
      next.delete(id);
      reportWorkingState(next, "removed_bucket", composedBlockTargetId(blockIndex, "item", id));
      return next;
    });
  };

  const handleCheck = () => {
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    const wrong = items.filter(
      (item) => normalizeAnswer(placements.get(item.id) ?? "") !== normalizeAnswer(item.bucket)
    );
    const correct = wrong.length === 0;
    setDone(correct);
    setLastWrong(!correct);
    const summary = items
      .map((item) => `${item.text} → ${placements.get(item.id) ?? "?"}`)
      .join(", ");
    onEvent({
      response: correct ? summary : `${summary} (${wrong.length} misplaced)`,
      correct,
      expected: items.map((item) => `${item.text} -> ${item.bucket}`).join(", "),
      attempt: nextAttempt,
    });
  };

  return (
    <div>
      <BlockLabel label={block.label} />
      {unplaced.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {unplaced.map((item) => {
            const targetId = composedBlockTargetId(blockIndex, "item", item.id);
            return (
              <motion.button
                key={item.id}
                whileTap={{ scale: 0.94 }}
                data-whiteboard-target-id={targetId}
                onClick={() => setActiveItem(activeItem === item.id ? null : item.id)}
                className={`${chipBase} ${
                  activeItem === item.id ? chipActive : chipIdle
                } ${getTutorTargetClass(item.text, renderState, targetId)}`}
                title={getTutorTargetTitle(item.text, renderState, targetId)}
              >
                {item.text}
              </motion.button>
            );
          })}
        </div>
      )}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {block.buckets.map((bucket, bucketIndex) => (
          <button
            key={bucket}
            data-whiteboard-target-id={composedBlockTargetId(blockIndex, "bucket", bucketIndex)}
            onClick={() => placeActive(bucket)}
            disabled={activeItem === null || done}
            className={`min-h-[4.5rem] rounded-xl border p-2 text-left transition-colors ${
              activeItem !== null && !done
                ? "border-[#FD954F]/50 bg-[#FD954F]/5 cursor-pointer"
                : done
                  ? "border-[#22C55E]/40 bg-[#22C55E]/5"
                  : "border-white/15 bg-white/[0.03]"
            }`}
          >
            <span className="block text-xs font-bold uppercase tracking-wider text-[#FD954F]">
              {bucket}
            </span>
            <span className="mt-1 flex flex-wrap gap-1.5">
              {items
                .filter((item) => placements.get(item.id) === bucket)
                .map((item) => {
                  const targetId = composedBlockTargetId(blockIndex, "item", item.id);
                  return (
                    <span
                      key={item.id}
                      data-whiteboard-target-id={targetId}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        unplace(item.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") unplace(item.id);
                      }}
                      className={`rounded-md border px-2 py-0.5 text-sm font-semibold ${
                        done ? chipCorrect : lastWrong ? chipWrong : chipActive
                      } ${getTutorTargetClass(item.text, renderState, targetId)}`}
                      title={getTutorTargetTitle(item.text, renderState, targetId)}
                    >
                      {item.text}
                    </span>
                  );
                })}
            </span>
          </button>
        ))}
      </div>
      {!done && (
        <div className="mt-3 flex items-center gap-2">
          <CheckButton disabled={!allPlaced} onClick={handleCheck} />
          {lastWrong && (
            <span className="text-sm font-medium text-[#FCA5A5]">
              Some are in the wrong bucket — tap them to take them out.
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// ─── highlight_target ────────────────────────────────────────────────

export const HighlightTargetBlock: React.FC<
  {
    block: z.infer<typeof HighlightTargetBlockSchema>;
    onEvent: OnBlockEvent;
  } & RenderAwareBlockProps
> = ({ block, renderState, blockIndex = 0, onEvent }) => {
  const words = useMemo(() => block.text.split(/\s+/).filter(Boolean), [block.text]);
  const targetSet = useMemo(() => new Set(block.targets.map(normalizeAnswer)), [block.targets]);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [done, setDone] = useState(false);
  const [lastWrong, setLastWrong] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const toggle = (index: number) => {
    if (done) return;
    setLastWrong(false);
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const handleCheck = () => {
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    const pickedWords = new Set([...picked].map((index) => normalizeAnswer(words[index])));
    const correct =
      pickedWords.size === targetSet.size && [...pickedWords].every((w) => targetSet.has(w));
    setDone(correct);
    setLastWrong(!correct);
    onEvent({
      response: [...picked].map((index) => words[index]).join(", ") || "(nothing)",
      correct,
      expected: block.targets.join(", "),
      attempt: nextAttempt,
    });
  };

  return (
    <div>
      <BlockLabel label={block.label} />
      <p className="text-lg leading-loose font-medium text-[#F9F9F9]">
        {words.map((word, index) => {
          const isPicked = picked.has(index);
          const isTarget = targetSet.has(normalizeAnswer(word));
          const targetId = composedBlockTargetId(blockIndex, "word", index);
          const tutorClass = getTutorTargetClass(word, renderState, targetId);
          return (
            <React.Fragment key={index}>
              <button
                data-whiteboard-target-id={targetId}
                onClick={() => toggle(index)}
                disabled={done}
                className={`rounded-md px-1 py-0.5 transition-colors ${
                  done && isTarget
                    ? "bg-[#22C55E]/20 text-[#4ADE80]"
                    : isPicked
                      ? "bg-[#FD954F]/25 text-[#FFD9B8]"
                      : "hover:bg-white/10"
                } ${tutorClass}`}
                title={getTutorTargetTitle(word, renderState, targetId)}
              >
                {word}
              </button>{" "}
            </React.Fragment>
          );
        })}
      </p>
      {!done && (
        <div className="mt-3 flex items-center gap-2">
          <CheckButton disabled={picked.size === 0} onClick={handleCheck} />
          {lastWrong && (
            <span className="text-sm font-medium text-[#FCA5A5]">Not quite — keep looking.</span>
          )}
        </div>
      )}
    </div>
  );
};

// ─── ordering ────────────────────────────────────────────────────────

export const OrderingBlock: React.FC<
  {
    block: z.infer<typeof OrderingBlockSchema>;
    onEvent: OnBlockEvent;
  } & RenderAwareBlockProps
> = ({ block, renderState, blockIndex = 0, onEvent }) => {
  const pool = useMemo(() => {
    const indexed = block.items.map((text, id) => ({ id, text }));
    const result = shuffled(indexed);
    // Don't present the answer pre-solved.
    if (result.every((item, i) => item.id === i) && result.length > 1) {
      [result[0], result[1]] = [result[1], result[0]];
    }
    return result;
  }, [block]);
  const [orderedIds, setOrderedIds] = useState<number[]>([]);
  const [done, setDone] = useState(false);
  const [lastWrong, setLastWrong] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const remaining = pool.filter((item) => !orderedIds.includes(item.id));
  const allPlaced = remaining.length === 0;

  const reportWorkingState = (
    nextOrderedIds: number[],
    action: "ordered_item" | "removed_ordered_item" | "reset_ordering",
    targetId?: string
  ) => {
    onEvent({
      event: "working",
      response: nextOrderedIds.map((id) => block.items[id]).join(" -> ") || "(empty)",
      action,
      target: block.label,
      targetId,
      expected: block.items.join(" -> "),
      progress: `${nextOrderedIds.length}/${block.items.length} items ordered`,
      resolved: false,
    });
  };

  const handleCheck = () => {
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    const correct = orderedIds.every((id, position) => id === position);
    setDone(correct);
    setLastWrong(!correct);
    onEvent({
      response: orderedIds.map((id) => block.items[id]).join(" → "),
      correct,
      expected: block.items.join(" → "),
      attempt: nextAttempt,
    });
  };

  return (
    <div>
      <BlockLabel label={block.label} />
      <ol
        className={`min-h-[3rem] space-y-1.5 rounded-xl border p-2.5 ${
          done
            ? "border-[#22C55E]/50 bg-[#22C55E]/10"
            : lastWrong
              ? "border-[#EF4444]/50 bg-[#EF4444]/10"
              : "border-dashed border-white/20 bg-white/[0.03]"
        }`}
      >
        {orderedIds.length === 0 && (
          <li className="text-sm text-[#8E8E93]">Tap the items below in order…</li>
        )}
        {orderedIds.map((id, position) => (
          <li key={id}>
            <button
              data-whiteboard-target-id={composedBlockTargetId(blockIndex, "item", id)}
              onClick={() => {
                if (done) return;
                setLastWrong(false);
                setOrderedIds((prev) => {
                  const next = prev.filter((existing) => existing !== id);
                  reportWorkingState(
                    next,
                    "removed_ordered_item",
                    composedBlockTargetId(blockIndex, "item", id)
                  );
                  return next;
                });
              }}
              disabled={done}
              className={`w-full rounded-lg border px-3 py-1.5 text-left text-base font-semibold ${
                done ? chipCorrect : chipActive
              } ${getTutorTargetClass(
                block.items[id],
                renderState,
                composedBlockTargetId(blockIndex, "item", id)
              )}`}
              title={getTutorTargetTitle(
                block.items[id],
                renderState,
                composedBlockTargetId(blockIndex, "item", id)
              )}
            >
              <span className="mr-2 text-xs font-bold text-[#8E8E93]">{position + 1}.</span>
              {block.items[id]}
            </button>
          </li>
        ))}
      </ol>
      {!done && (
        <>
          {remaining.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {remaining.map((item) => {
                const targetId = composedBlockTargetId(blockIndex, "item", item.id);
                return (
                  <motion.button
                    key={item.id}
                    whileTap={{ scale: 0.94 }}
                    data-whiteboard-target-id={targetId}
                    onClick={() => {
                      setLastWrong(false);
                      setOrderedIds((prev) => {
                        const next = [...prev, item.id];
                        reportWorkingState(next, "ordered_item", targetId);
                        return next;
                      });
                    }}
                    className={`${chipBase} ${chipIdle} ${getTutorTargetClass(
                      item.text,
                      renderState,
                      targetId
                    )}`}
                    title={getTutorTargetTitle(item.text, renderState, targetId)}
                  >
                    {item.text}
                  </motion.button>
                );
              })}
            </div>
          )}
          <div className="mt-3 flex items-center gap-2">
            <CheckButton disabled={!allPlaced} onClick={handleCheck} />
            {allPlaced && orderedIds.length > 0 && !done && (
              <button
                onClick={() => {
                  setOrderedIds([]);
                  setLastWrong(false);
                  reportWorkingState([], "reset_ordering");
                }}
                className="flex items-center gap-1.5 rounded-xl border border-white/15 px-3.5 py-2 text-sm font-semibold text-[#AEAEB2] transition-colors hover:border-white/30 hover:text-white"
              >
                <RotateCcw size={14} />
                Reset
              </button>
            )}
            {lastWrong && (
              <span className="text-sm font-medium text-[#FCA5A5]">
                Order&apos;s off — tap an item to remove it.
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ─── slider ──────────────────────────────────────────────────────────

export const SliderBlock: React.FC<{
  block: z.infer<typeof SliderBlockSchema>;
  onEvent: OnBlockEvent;
}> = ({ block, onEvent }) => {
  const steps = block.steps;
  const max = steps ? steps.length - 1 : 100;
  const [value, setValue] = useState(Math.round(max / 2));
  const attemptRef = useRef(0);

  const describe = (v: number) => (steps ? steps[v] : `${v}%`);

  const commit = () => {
    attemptRef.current += 1;
    onEvent({
      response: describe(value),
      action: "set_slider",
      target: block.label,
      attempt: attemptRef.current,
    });
  };

  return (
    <div>
      <BlockLabel label={block.label} />
      <input
        type="range"
        min={0}
        max={max}
        step={1}
        value={value}
        onChange={(e) => setValue(Number(e.currentTarget.value))}
        onPointerUp={commit}
        onKeyUp={(e) => {
          if (e.key === "ArrowLeft" || e.key === "ArrowRight") commit();
        }}
        className="w-full accent-[#FD954F]"
        aria-label={block.label}
      />
      <div className="mt-1 flex items-center justify-between text-xs font-semibold text-[#AEAEB2]">
        <span>{block.leftLabel}</span>
        <span className="rounded-md bg-[#FD954F]/15 px-2 py-0.5 font-bold text-[#FFD9B8]">
          {describe(value)}
        </span>
        <span>{block.rightLabel}</span>
      </div>
    </div>
  );
};

// ─── reveal ──────────────────────────────────────────────────────────

export const SentenceRepairBlock: React.FC<
  {
    block: z.infer<typeof SentenceRepairBlockSchema>;
    onEvent: OnBlockEvent;
  } & RenderAwareBlockProps
> = ({ block, renderState, blockIndex = 0, onEvent }) => {
  const [choices, setChoices] = useState<Map<number, string>>(new Map());
  const [done, setDone] = useState(false);
  const [lastWrong, setLastWrong] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const allChosen = choices.size === block.targets.length;

  const handlePick = (index: number, option: string) => {
    if (done) return;
    setLastWrong(false);
    setChoices((prev) => new Map(prev).set(index, option));
  };

  const handleCheck = () => {
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    const correct = block.targets.every(
      (target, index) =>
        normalizeAnswer(choices.get(index) ?? "") === normalizeAnswer(target.answer)
    );
    const selectedOptionTargetIds = block.targets
      .map((target, index) => {
        const choice = choices.get(index);
        const optionIndex = target.options.findIndex((option) => option === choice);
        return optionIndex >= 0
          ? composedBlockTargetId(blockIndex, "option", index, optionIndex)
          : undefined;
      })
      .filter(Boolean);
    setDone(correct);
    setLastWrong(!correct);
    onEvent({
      response: block.targets
        .map((target, index) => `${target.text} -> ${choices.get(index) ?? "?"}`)
        .join("; "),
      action: "sentence_repair",
      target: block.original,
      targetId: selectedOptionTargetIds.length === 1 ? selectedOptionTargetIds[0] : undefined,
      correct,
      expected: block.targets.map((target) => `${target.text} -> ${target.answer}`).join("; "),
      attempt: nextAttempt,
    });
  };

  return (
    <div>
      <BlockLabel label={block.label} />
      <div className="rounded-xl border border-white/15 bg-white/[0.04] p-3">
        <p className="text-xs font-bold uppercase tracking-wider text-[#AEAEB2]">Learner said</p>
        <WhiteboardWriteInText
          as="p"
          text={block.original}
          data-whiteboard-target-id={composedBlockTargetId(blockIndex, "original")}
          className={`mt-1 text-base font-medium leading-relaxed text-[#F9F9F9] ${getTutorTargetClass(
            block.original,
            renderState,
            composedBlockTargetId(blockIndex, "original")
          )}`}
          title={getTutorTargetTitle(
            block.original,
            renderState,
            composedBlockTargetId(blockIndex, "original")
          )}
        />
      </div>
      <div className="mt-3 space-y-3">
        {block.targets.map((target, index) => {
          const choice = choices.get(index);
          const targetId = composedBlockTargetId(blockIndex, "target", index);
          return (
            <div key={`${target.text}-${index}`} className="rounded-xl border border-white/10 p-3">
              <WhiteboardWriteInText
                as="p"
                text={target.text}
                data-whiteboard-target-id={targetId}
                className={`text-sm font-semibold text-[#FFD9B8] ${getTutorTargetClass(
                  target.text,
                  renderState,
                  targetId
                )}`}
                title={getTutorTargetTitle(target.text, renderState, targetId)}
              />
              <div className="mt-2 flex flex-wrap gap-2">
                {target.options.map((option, optionIndex) => {
                  const picked = choice === option;
                  const isAnswer =
                    done && normalizeAnswer(option) === normalizeAnswer(target.answer);
                  const optionTargetId = composedBlockTargetId(
                    blockIndex,
                    "option",
                    index,
                    optionIndex
                  );
                  return (
                    <button
                      key={option}
                      data-whiteboard-target-id={optionTargetId}
                      onClick={() => handlePick(index, option)}
                      disabled={done}
                      aria-pressed={picked}
                      className={`${chipBase} ${
                        isAnswer ? chipCorrect : picked ? chipActive : chipIdle
                      } ${getTutorTargetClass(option, renderState, optionTargetId)} ${
                        done && !isAnswer && picked ? chipWrong : ""
                      }`}
                      title={getTutorTargetTitle(option, renderState, optionTargetId)}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
              {target.note && <p className="mt-2 text-sm text-[#AEAEB2]">{target.note}</p>}
            </div>
          );
        })}
      </div>
      {!done && (
        <div className="mt-3 flex items-center gap-2">
          <CheckButton disabled={!allChosen} onClick={handleCheck} />
          {lastWrong && <span className="text-sm font-medium text-[#FCA5A5]">Try one change.</span>}
        </div>
      )}
      {done && block.corrected && (
        <p className="mt-3 rounded-xl border border-[#22C55E]/35 bg-[#22C55E]/10 px-3 py-2 text-base font-semibold text-[#4ADE80]">
          {block.corrected}
        </p>
      )}
    </div>
  );
};

export const PairMatchBlock: React.FC<
  {
    block: z.infer<typeof PairMatchBlockSchema>;
    onEvent: OnBlockEvent;
  } & RenderAwareBlockProps
> = ({ block, renderState, blockIndex = 0, onEvent }) => {
  const rightItems = useMemo(() => shuffled(block.rightItems), [block.rightItems]);
  const [selectedLeft, setSelectedLeft] = useState<string | null>(null);
  const [matches, setMatches] = useState<Map<string, string>>(new Map());
  const [done, setDone] = useState(false);
  const [lastWrong, setLastWrong] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const matchRight = (rightId: string) => {
    if (!selectedLeft || done) return;
    setLastWrong(false);
    setMatches((prev) => {
      const next = new Map(prev).set(selectedLeft, rightId);
      const rightById = new Map(block.rightItems.map((item) => [item.id, item]));
      onEvent({
        event: "working",
        response:
          block.leftItems
            .filter((left) => next.has(left.id))
            .map((left) => `${left.text} -> ${rightById.get(next.get(left.id) ?? "")?.text ?? "?"}`)
            .join("; ") || "(empty)",
        action: "matched_pair",
        target: block.label,
        targetId: composedBlockTargetId(
          blockIndex,
          "left",
          Math.max(
            block.leftItems.findIndex((left) => left.id === selectedLeft),
            0
          )
        ),
        expected: block.leftItems
          .map(
            (left) =>
              `${left.text} -> ${block.rightItems.find((right) => right.leftId === left.id)?.text ?? "?"}`
          )
          .join("; "),
        progress: `${next.size}/${block.leftItems.length} pairs matched`,
        resolved: false,
      });
      return next;
    });
    setSelectedLeft(null);
  };

  const handleCheck = () => {
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    const rightById = new Map(block.rightItems.map((item) => [item.id, item]));
    const correct = block.leftItems.every(
      (left) => rightById.get(matches.get(left.id) ?? "")?.leftId === left.id
    );
    setDone(correct);
    setLastWrong(!correct);
    onEvent({
      response: block.leftItems
        .map((left) => `${left.text} -> ${rightById.get(matches.get(left.id) ?? "")?.text ?? "?"}`)
        .join("; "),
      correct,
      expected: block.leftItems
        .map(
          (left) =>
            `${left.text} -> ${block.rightItems.find((right) => right.leftId === left.id)?.text ?? "?"}`
        )
        .join("; "),
      attempt: nextAttempt,
    });
  };

  return (
    <div>
      <BlockLabel label={block.label} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#AEAEB2]">
            {block.leftLabel ?? "Prompt"}
          </p>
          <div className="space-y-2">
            {block.leftItems.map((item, index) => {
              const targetId = composedBlockTargetId(blockIndex, "left", index);
              return (
                <button
                  key={item.id}
                  data-whiteboard-target-id={targetId}
                  onClick={() => {
                    if (!done) setSelectedLeft(selectedLeft === item.id ? null : item.id);
                  }}
                  disabled={done}
                  aria-pressed={selectedLeft === item.id}
                  className={`w-full rounded-lg border px-3 py-2 text-left text-sm font-semibold ${
                    selectedLeft === item.id
                      ? chipActive
                      : matches.has(item.id)
                        ? chipCorrect
                        : chipIdle
                  } ${getTutorTargetClass(item.text, renderState, targetId)}`}
                  title={getTutorTargetTitle(item.text, renderState, targetId)}
                >
                  {item.text}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#AEAEB2]">
            {block.rightLabel ?? "Match"}
          </p>
          <div className="space-y-2">
            {rightItems.map((item) => {
              const matched = [...matches.values()].includes(item.id);
              const sourceIndex = block.rightItems.findIndex((right) => right.id === item.id);
              const targetId = composedBlockTargetId(
                blockIndex,
                "right",
                sourceIndex >= 0 ? sourceIndex : 0
              );
              return (
                <button
                  key={item.id}
                  data-whiteboard-target-id={targetId}
                  onClick={() => matchRight(item.id)}
                  disabled={done || !selectedLeft}
                  className={`w-full rounded-lg border px-3 py-2 text-left text-sm font-semibold ${
                    matched ? chipCorrect : chipIdle
                  } ${getTutorTargetClass(item.text, renderState, targetId)} disabled:opacity-60`}
                  title={getTutorTargetTitle(item.text, renderState, targetId)}
                >
                  {item.text}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {!done && (
        <div className="mt-3 flex items-center gap-2">
          <CheckButton disabled={matches.size !== block.leftItems.length} onClick={handleCheck} />
          {lastWrong && (
            <span className="text-sm font-medium text-[#FCA5A5]">Some matches are off.</span>
          )}
        </div>
      )}
    </div>
  );
};

export const PronunciationFocusBlock: React.FC<
  {
    block: z.infer<typeof PronunciationFocusBlockSchema>;
    onEvent: OnBlockEvent;
  } & RenderAwareBlockProps
> = ({ block, renderState, blockIndex = 0, onEvent }) => {
  const [selected, setSelected] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [practiced, setPracticed] = useState(false);

  const commit = () => {
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    setPracticed(true);
    onEvent({
      response: selected ?? block.focus,
      action: "confirmed_pronunciation_practice",
      target: block.phrase,
      expected: block.focus,
      resolved: true,
      attempt: nextAttempt,
    });
  };

  return (
    <div>
      <BlockLabel label={block.label} />
      <div className="rounded-xl border border-[#FD954F]/30 bg-[#FD954F]/10 p-3">
        <p className="text-xs font-bold uppercase tracking-wider text-[#AEAEB2]">
          Focus: {block.focus}
        </p>
        <WhiteboardWriteInText
          as="p"
          text={block.phrase}
          data-whiteboard-target-id={composedBlockTargetId(blockIndex, "phrase")}
          className={`mt-2 text-xl font-bold leading-snug text-[#F9F9F9] ${getTutorTargetClass(
            block.phrase,
            renderState,
            composedBlockTargetId(blockIndex, "phrase")
          )}`}
          title={getTutorTargetTitle(
            block.phrase,
            renderState,
            composedBlockTargetId(blockIndex, "phrase")
          )}
        />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {block.targets.map((target, index) => {
          const isSelected = selected === target.text;
          const targetId = composedBlockTargetId(blockIndex, "target", index);
          return (
            <button
              key={target.text}
              data-whiteboard-target-id={targetId}
              onClick={() => setSelected(target.text)}
              aria-pressed={isSelected}
              className={`${chipBase} ${
                isSelected ? chipActive : chipIdle
              } ${getTutorTargetClass(target.text, renderState, targetId)}`}
              title={getTutorTargetTitle(target.text, renderState, targetId)}
            >
              <span className="block">{target.text}</span>
              {target.note && (
                <span className="mt-0.5 block text-xs font-medium text-[#AEAEB2]">
                  {target.note}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <button
        onClick={commit}
        disabled={!selected}
        className="mt-3 flex items-center gap-1.5 rounded-xl bg-[#FD954F] px-3.5 py-2 text-sm font-bold text-white transition-colors hover:bg-[#F28747] disabled:bg-[#3A3A3C] disabled:text-[#8E8E93]"
      >
        <Mic2 size={15} />
        {practiced ? "Practiced again" : "I practiced it"}
      </button>
    </div>
  );
};

export const ListeningDiscriminationBlock: React.FC<
  {
    block: z.infer<typeof ListeningDiscriminationBlockSchema>;
    onEvent: OnBlockEvent;
  } & RenderAwareBlockProps
> = ({ block, renderState, blockIndex = 0, onEvent }) => {
  const [picked, setPicked] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const choose = (option: string) => {
    if (done) return;
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    setPicked(option);
    const correct = normalizeAnswer(option) === normalizeAnswer(block.answer);
    const optionIndex = block.options.findIndex((candidate) => candidate === option);
    setDone(correct);
    onEvent({
      response: option,
      action: "heard_choice",
      target: block.prompt ?? block.label ?? "listening discrimination",
      targetId:
        optionIndex >= 0 ? composedBlockTargetId(blockIndex, "option", optionIndex) : undefined,
      expected: block.answer,
      correct,
      resolved: correct,
      attempt: nextAttempt,
    });
  };

  return (
    <div>
      <BlockLabel label={block.label} />
      <div className="rounded-xl border border-white/15 bg-white/[0.04] p-3">
        <p className="flex items-center gap-2 text-sm font-semibold text-[#FFD9B8]">
          <Ear size={16} />
          <WhiteboardWriteInText text={block.prompt ?? "What did you hear?"} inline />
        </p>
        {block.note && (
          <WhiteboardWriteInText
            as="p"
            text={block.note}
            initialDelayMs={360}
            className="mt-2 text-sm text-[#AEAEB2]"
          />
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {block.options.map((option, index) => {
          const selected = picked === option;
          const isAnswer = done && normalizeAnswer(option) === normalizeAnswer(block.answer);
          const targetId = composedBlockTargetId(blockIndex, "option", index);
          return (
            <button
              key={option}
              data-whiteboard-target-id={targetId}
              onClick={() => choose(option)}
              disabled={done}
              aria-pressed={selected}
              className={`${chipBase} ${
                isAnswer ? chipCorrect : selected ? chipActive : chipIdle
              } ${getTutorTargetClass(option, renderState, targetId)} ${
                selected && !isAnswer && picked ? chipWrong : ""
              }`}
              title={getTutorTargetTitle(option, renderState, targetId)}
            >
              {option}
            </button>
          );
        })}
      </div>
      {picked && !done && (
        <p className="mt-2 text-sm font-medium text-[#FCA5A5]">
          Not quite. Listen once more, then tap another option.
        </p>
      )}
    </div>
  );
};

export const PhraseBankRoleplayBlock: React.FC<
  {
    block: z.infer<typeof PhraseBankRoleplayBlockSchema>;
    onEvent: OnBlockEvent;
  } & RenderAwareBlockProps
> = ({ block, renderState, blockIndex = 0, onEvent }) => {
  const [picked, setPicked] = useState<string | null>(null);
  const recommended = block.phrases.find((phrase) => phrase.recommended);

  const pick = (phrase: (typeof block.phrases)[number]) => {
    const correct = recommended
      ? normalizeAnswer(phrase.text) === normalizeAnswer(recommended.text)
      : undefined;
    setPicked(phrase.text);
    const phraseIndex = block.phrases.findIndex((candidate) => candidate.text === phrase.text);
    onEvent({
      response: `selected: ${phrase.text}${phrase.function ? ` (${phrase.function})` : ""}`,
      action: "selected_roleplay_phrase",
      target: block.goal,
      targetId:
        phraseIndex >= 0 ? composedBlockTargetId(blockIndex, "phrase", phraseIndex) : undefined,
      correct,
      expected: recommended?.text,
      resolved: recommended ? correct : true,
      attempt: 1,
    });
  };

  return (
    <div>
      <BlockLabel label={block.label} />
      <div className="rounded-xl border border-white/15 bg-white/[0.04] p-3">
        <WhiteboardWriteInText
          as="p"
          text={block.scenario}
          className="text-xs font-bold uppercase tracking-wider text-[#AEAEB2]"
        />
        {block.tutorLine && (
          <WhiteboardWriteInText
            as="p"
            text={block.tutorLine}
            initialDelayMs={260}
            className="mt-2 text-base font-medium text-[#F9F9F9]"
          />
        )}
        <WhiteboardWriteInText
          as="p"
          text={`Goal: ${block.goal}`}
          initialDelayMs={420}
          className="mt-2 text-sm text-[#FFD9B8]"
        />
      </div>
      <div className="mt-3 space-y-2">
        {block.phrases.map((phrase, index) => {
          const isPicked = picked === phrase.text;
          const targetId = composedBlockTargetId(blockIndex, "phrase", index);
          return (
            <button
              key={phrase.text}
              data-whiteboard-target-id={targetId}
              onClick={() => pick(phrase)}
              className={`w-full rounded-xl border px-3 py-2 text-left transition-colors ${
                isPicked
                  ? "border-[#22C55E]/60 bg-[#22C55E]/15"
                  : "border-white/15 bg-white/[0.05] hover:border-[#FD954F]/55"
              } ${getTutorTargetClass(phrase.text, renderState, targetId)}`}
              title={getTutorTargetTitle(phrase.text, renderState, targetId)}
            >
              <span className="block text-base font-semibold text-[#F9F9F9]">{phrase.text}</span>
              <span className="mt-1 block text-xs text-[#AEAEB2]">
                {[phrase.function, phrase.register, phrase.translation].filter(Boolean).join(" - ")}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const TransformationFrameBlock: React.FC<
  {
    block: z.infer<typeof TransformationFrameBlockSchema>;
    onEvent: OnBlockEvent;
  } & RenderAwareBlockProps
> = ({ block, renderState, blockIndex = 0, onEvent }) => {
  const [choices, setChoices] = useState<Map<number, string>>(new Map());
  const [done, setDone] = useState(false);
  const [lastWrong, setLastWrong] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const allChosen = choices.size === block.items.length;

  const handlePick = (index: number, option: string) => {
    if (done) return;
    setLastWrong(false);
    setChoices((prev) => {
      const next = new Map(prev).set(index, option);
      const optionIndex =
        block.items[index]?.options.findIndex((candidate) => candidate === option) ?? -1;
      onEvent({
        event: "working",
        response: block.items
          .map((item, itemIndex) => `${item.cue} => ${next.get(itemIndex) ?? "?"}`)
          .join("; "),
        action: "selected_transformation",
        target: block.instruction,
        targetId:
          optionIndex >= 0
            ? composedBlockTargetId(blockIndex, "option", index, optionIndex)
            : composedBlockTargetId(blockIndex, "cue", index),
        expected: block.items.map((item) => `${item.cue} => ${item.answer}`).join("; "),
        progress: `${next.size}/${block.items.length} transformations selected`,
        resolved: false,
      });
      return next;
    });
  };

  const handleCheck = () => {
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    const correct = block.items.every(
      (item, index) => normalizeAnswer(choices.get(index) ?? "") === normalizeAnswer(item.answer)
    );
    const selectedOptionTargetIds = block.items
      .map((item, index) => {
        const choice = choices.get(index);
        const optionIndex = item.options.findIndex((option) => option === choice);
        return optionIndex >= 0
          ? composedBlockTargetId(blockIndex, "option", index, optionIndex)
          : undefined;
      })
      .filter(Boolean);
    setDone(correct);
    setLastWrong(!correct);
    onEvent({
      response: block.items
        .map((item, index) => `${item.cue} => ${choices.get(index) ?? "?"}`)
        .join("; "),
      action: "completed_transformation_frame",
      target: block.base,
      targetId:
        selectedOptionTargetIds.length === 1
          ? selectedOptionTargetIds[0]
          : composedBlockTargetId(blockIndex, "base"),
      correct,
      expected: block.items.map((item) => `${item.cue} => ${item.answer}`).join("; "),
      resolved: correct,
      attempt: nextAttempt,
    });
  };

  return (
    <div>
      <BlockLabel label={block.label} />
      <div className="rounded-xl border border-white/15 bg-white/[0.04] p-3">
        <WhiteboardWriteInText
          as="p"
          text={block.instruction}
          className="text-sm font-semibold text-[#FFD9B8]"
        />
        <WhiteboardWriteInText
          as="p"
          text={block.base}
          data-whiteboard-target-id={composedBlockTargetId(blockIndex, "base")}
          className={`mt-2 text-base font-medium text-[#F9F9F9] ${getTutorTargetClass(
            block.base,
            renderState,
            composedBlockTargetId(blockIndex, "base")
          )}`}
          title={getTutorTargetTitle(
            block.base,
            renderState,
            composedBlockTargetId(blockIndex, "base")
          )}
        />
      </div>
      <div className="mt-3 space-y-3">
        {block.items.map((item, index) => {
          const choice = choices.get(index);
          const cueTargetId = composedBlockTargetId(blockIndex, "cue", index);
          return (
            <div key={`${item.cue}-${index}`} className="rounded-xl border border-white/10 p-3">
              <p
                data-whiteboard-target-id={cueTargetId}
                className={`text-xs font-bold uppercase tracking-wider text-[#AEAEB2] ${getTutorTargetClass(
                  item.cue,
                  renderState,
                  cueTargetId
                )}`}
                title={getTutorTargetTitle(item.cue, renderState, cueTargetId)}
              >
                {item.cue}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {item.options.map((option, optionIndex) => {
                  const picked = choice === option;
                  const isAnswer = done && normalizeAnswer(option) === normalizeAnswer(item.answer);
                  const optionTargetId = composedBlockTargetId(
                    blockIndex,
                    "option",
                    index,
                    optionIndex
                  );
                  return (
                    <button
                      key={option}
                      data-whiteboard-target-id={optionTargetId}
                      onClick={() => handlePick(index, option)}
                      disabled={done}
                      aria-pressed={picked}
                      className={`${chipBase} ${
                        isAnswer ? chipCorrect : picked ? chipActive : chipIdle
                      } ${getTutorTargetClass(option, renderState, optionTargetId)} ${
                        done && !isAnswer && picked ? chipWrong : ""
                      }`}
                      title={getTutorTargetTitle(option, renderState, optionTargetId)}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
              {item.note && <p className="mt-2 text-sm text-[#AEAEB2]">{item.note}</p>}
            </div>
          );
        })}
      </div>
      {!done && (
        <div className="mt-3 flex items-center gap-2">
          <CheckButton disabled={!allChosen} onClick={handleCheck} />
          {lastWrong && (
            <span className="text-sm font-medium text-[#FCA5A5]">One transformation is off.</span>
          )}
        </div>
      )}
    </div>
  );
};

export const MorphologyGridBlock: React.FC<
  {
    block: z.infer<typeof MorphologyGridBlockSchema>;
    onEvent: OnBlockEvent;
  } & RenderAwareBlockProps
> = ({ block, renderState, blockIndex = 0, onEvent }) => {
  const [choices, setChoices] = useState<Map<string, string>>(new Map());
  const [done, setDone] = useState(false);
  const [lastWrong, setLastWrong] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const totalCells = block.rows.reduce((count, row) => count + row.cells.length, 0);
  const allChosen = choices.size === totalCells;

  const keyFor = (rowIndex: number, cellIndex: number) => `${rowIndex}:${cellIndex}`;

  const choose = (rowIndex: number, cellIndex: number, option: string) => {
    if (done) return;
    setLastWrong(false);
    setChoices((prev) => {
      const next = new Map(prev).set(keyFor(rowIndex, cellIndex), option);
      const optionIndex =
        block.rows[rowIndex]?.cells[cellIndex]?.options.findIndex(
          (candidate) => candidate === option
        ) ?? -1;
      const response = block.rows
        .flatMap((row, currentRowIndex) =>
          row.cells.map(
            (cell, currentCellIndex) =>
              `${row.label} ${cell.cue} => ${next.get(keyFor(currentRowIndex, currentCellIndex)) ?? "?"}`
          )
        )
        .join("; ");
      const expected = block.rows
        .flatMap((row) => row.cells.map((cell) => `${row.label} ${cell.cue} => ${cell.answer}`))
        .join("; ");

      onEvent({
        event: "working",
        response,
        action: "selected_morphology_form",
        target: block.pattern,
        targetId:
          optionIndex >= 0
            ? composedBlockTargetId(blockIndex, "option", rowIndex * 10 + cellIndex, optionIndex)
            : composedBlockTargetId(blockIndex, "cue", rowIndex, cellIndex),
        expected,
        progress: `${next.size}/${totalCells} cells completed`,
        resolved: false,
      });
      return next;
    });
  };

  const handleCheck = () => {
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    const correct = block.rows.every((row, rowIndex) =>
      row.cells.every(
        (cell, cellIndex) =>
          normalizeAnswer(choices.get(keyFor(rowIndex, cellIndex)) ?? "") ===
          normalizeAnswer(cell.answer)
      )
    );
    setDone(correct);
    setLastWrong(!correct);
    const response = block.rows
      .flatMap((row, rowIndex) =>
        row.cells.map(
          (cell, cellIndex) =>
            `${row.label} ${cell.cue} => ${choices.get(keyFor(rowIndex, cellIndex)) ?? "?"}`
        )
      )
      .join("; ");
    const expected = block.rows
      .flatMap((row) => row.cells.map((cell) => `${row.label} ${cell.cue} => ${cell.answer}`))
      .join("; ");
    const selectedOptionTargetIds = block.rows
      .flatMap((row, rowIndex) =>
        row.cells.map((cell, cellIndex) => {
          const choice = choices.get(keyFor(rowIndex, cellIndex));
          const optionIndex = cell.options.findIndex((option) => option === choice);
          return optionIndex >= 0
            ? composedBlockTargetId(blockIndex, "option", rowIndex * 10 + cellIndex, optionIndex)
            : undefined;
        })
      )
      .filter(Boolean);

    onEvent({
      response,
      action: "completed_morphology_grid",
      target: block.pattern,
      targetId:
        selectedOptionTargetIds.length === 1
          ? selectedOptionTargetIds[0]
          : composedBlockTargetId(blockIndex, "pattern"),
      expected,
      correct,
      resolved: correct,
      attempt: nextAttempt,
    });
  };

  return (
    <div>
      <BlockLabel label={block.label} />
      <div className="rounded-xl border border-white/15 bg-white/[0.04] p-3">
        <p
          data-whiteboard-target-id={composedBlockTargetId(blockIndex, "pattern")}
          className="flex items-center gap-2 text-sm font-semibold text-[#FFD9B8]"
        >
          <Table2 size={16} />
          <WhiteboardWriteInText text={block.pattern} inline />
        </p>
      </div>
      <div className="mt-3 space-y-3">
        {block.rows.map((row, rowIndex) => (
          <div key={`${row.label}-${rowIndex}`} className="rounded-xl border border-white/10 p-3">
            {(() => {
              const rowTargetId = composedBlockTargetId(blockIndex, "row", rowIndex);
              return (
                <p
                  data-whiteboard-target-id={rowTargetId}
                  className={`text-xs font-bold uppercase tracking-wider text-[#AEAEB2] ${getTutorTargetClass(
                    row.label,
                    renderState,
                    rowTargetId
                  )}`}
                  title={getTutorTargetTitle(row.label, renderState, rowTargetId)}
                >
                  {row.label}
                </p>
              );
            })()}
            <div className="mt-2 grid gap-2">
              {row.cells.map((cell, cellIndex) => {
                const key = keyFor(rowIndex, cellIndex);
                const choice = choices.get(key);
                const cueTargetId = composedBlockTargetId(blockIndex, "cue", rowIndex, cellIndex);
                return (
                  <div key={`${cell.cue}-${cellIndex}`} className="rounded-lg bg-white/[0.03] p-2">
                    <p
                      data-whiteboard-target-id={cueTargetId}
                      className={`mb-2 text-sm font-semibold text-[#F9F9F9] ${getTutorTargetClass(
                        cell.cue,
                        renderState,
                        cueTargetId
                      )}`}
                      title={getTutorTargetTitle(cell.cue, renderState, cueTargetId)}
                    >
                      {cell.cue}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {cell.options.map((option, optionIndex) => {
                        const picked = choice === option;
                        const isAnswer =
                          done && normalizeAnswer(option) === normalizeAnswer(cell.answer);
                        const optionTargetId = composedBlockTargetId(
                          blockIndex,
                          "option",
                          rowIndex * 10 + cellIndex,
                          optionIndex
                        );
                        return (
                          <button
                            key={option}
                            data-whiteboard-target-id={optionTargetId}
                            onClick={() => choose(rowIndex, cellIndex, option)}
                            disabled={done}
                            aria-pressed={picked}
                            className={`${chipBase} ${
                              isAnswer ? chipCorrect : picked ? chipActive : chipIdle
                            } ${getTutorTargetClass(option, renderState, optionTargetId)} ${
                              done && picked && !isAnswer ? chipWrong : ""
                            }`}
                            title={getTutorTargetTitle(option, renderState, optionTargetId)}
                          >
                            {option}
                          </button>
                        );
                      })}
                    </div>
                    {done && cell.note && (
                      <p className="mt-2 text-sm text-[#AEAEB2]">{cell.note}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {!done && (
        <div className="mt-3 flex items-center gap-2">
          <CheckButton disabled={!allChosen} onClick={handleCheck} />
          {lastWrong && (
            <span className="text-sm font-medium text-[#FCA5A5]">One form is off.</span>
          )}
        </div>
      )}
    </div>
  );
};

export const ContrastPairBlock: React.FC<
  {
    block: z.infer<typeof ContrastPairBlockSchema>;
    onEvent: OnBlockEvent;
  } & RenderAwareBlockProps
> = ({ block, renderState, blockIndex = 0, onEvent }) => {
  const [choices, setChoices] = useState<Map<number, "left" | "right" | "both">>(new Map());
  const [done, setDone] = useState(false);
  const [lastWrong, setLastWrong] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const allChosen = choices.size === block.pairs.length;

  const choose = (index: number, value: "left" | "right" | "both") => {
    if (done) return;
    setLastWrong(false);
    setChoices((prev) => {
      const next = new Map(prev).set(index, value);
      onEvent({
        event: "working",
        response: block.pairs
          .map((pair, pairIndex) => `${pair.left} / ${pair.right} => ${next.get(pairIndex) ?? "?"}`)
          .join("; "),
        action: "selected_contrast_side",
        target: block.prompt,
        targetId:
          value === "left" || value === "right"
            ? composedBlockTargetId(blockIndex, value, index)
            : composedBlockTargetId(blockIndex, "both", index),
        expected: block.pairs
          .map((pair, pairIndex) => `${pair.left} / ${pair.right} => ${pair.answer}`)
          .join("; "),
        progress: `${next.size}/${block.pairs.length} contrasts selected`,
        resolved: false,
      });
      return next;
    });
  };

  const handleCheck = () => {
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    const correct = block.pairs.every((pair, index) => choices.get(index) === pair.answer);
    const selectedTargetIds = block.pairs
      .map((pair, index) => {
        const choice = choices.get(index);
        if (choice === "left" || choice === "right") {
          return composedBlockTargetId(blockIndex, choice, index);
        }
        return choice === "both" ? composedBlockTargetId(blockIndex, "both", index) : undefined;
      })
      .filter(Boolean);
    setDone(correct);
    setLastWrong(!correct);
    onEvent({
      response: block.pairs
        .map((pair, index) => `pair ${index + 1}: ${choices.get(index) ?? "?"}`)
        .join("; "),
      action: "completed_contrast_pair",
      target: block.prompt,
      targetId:
        selectedTargetIds.length === 1
          ? selectedTargetIds[0]
          : composedBlockTargetId(blockIndex, "prompt"),
      correct,
      expected: block.pairs.map((pair, index) => `pair ${index + 1}: ${pair.answer}`).join("; "),
      resolved: correct,
      attempt: nextAttempt,
    });
  };

  return (
    <div>
      <BlockLabel label={block.label} />
      <WhiteboardWriteInText
        as="p"
        text={block.prompt}
        data-whiteboard-target-id={composedBlockTargetId(blockIndex, "prompt")}
        className="whiteboard-tutor-mark-target whiteboard-tutor-highlight-target text-base font-semibold leading-snug text-[#F9F9F9]"
        title={`Target ${composedBlockTargetId(blockIndex, "prompt")}`}
      />
      <div className="mt-3 space-y-3">
        {block.pairs.map((pair, index) => {
          const choice = choices.get(index);
          const leftTargetId = composedBlockTargetId(blockIndex, "left", index);
          const rightTargetId = composedBlockTargetId(blockIndex, "right", index);
          return (
            <div
              key={`${pair.left}-${pair.right}-${index}`}
              className="rounded-xl border border-white/10 p-3"
            >
              {pair.focus && (
                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#AEAEB2]">
                  {pair.focus}
                </p>
              )}
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <button
                  data-whiteboard-target-id={leftTargetId}
                  onClick={() => choose(index, "left")}
                  disabled={done}
                  aria-pressed={choice === "left"}
                  className={`rounded-lg border px-3 py-2 text-left text-sm font-semibold ${
                    done && pair.answer === "left"
                      ? chipCorrect
                      : choice === "left"
                        ? chipActive
                        : chipIdle
                  } ${getTutorTargetClass(pair.left, renderState, leftTargetId)} ${
                    done && choice === "left" && pair.answer !== "left" ? chipWrong : ""
                  }`}
                  title={getTutorTargetTitle(pair.left, renderState, leftTargetId)}
                >
                  {pair.left}
                </button>
                <button
                  data-whiteboard-target-id={rightTargetId}
                  onClick={() => choose(index, "right")}
                  disabled={done}
                  aria-pressed={choice === "right"}
                  className={`rounded-lg border px-3 py-2 text-left text-sm font-semibold ${
                    done && pair.answer === "right"
                      ? chipCorrect
                      : choice === "right"
                        ? chipActive
                        : chipIdle
                  } ${getTutorTargetClass(pair.right, renderState, rightTargetId)} ${
                    done && choice === "right" && pair.answer !== "right" ? chipWrong : ""
                  }`}
                  title={getTutorTargetTitle(pair.right, renderState, rightTargetId)}
                >
                  {pair.right}
                </button>
              </div>
              <button
                data-whiteboard-target-id={composedBlockTargetId(blockIndex, "both", index)}
                onClick={() => choose(index, "both")}
                disabled={done}
                aria-pressed={choice === "both"}
                className={`mt-2 rounded-lg border px-3 py-1.5 text-sm font-semibold ${
                  done && pair.answer === "both"
                    ? chipCorrect
                    : choice === "both"
                      ? chipActive
                      : chipIdle
                } ${done && choice === "both" && pair.answer !== "both" ? chipWrong : ""}`}
              >
                Both work
              </button>
              {done && pair.explanation && (
                <p className="mt-2 text-sm leading-relaxed text-[#D1D1D6]">{pair.explanation}</p>
              )}
            </div>
          );
        })}
      </div>
      {!done && (
        <div className="mt-3 flex items-center gap-2">
          <CheckButton disabled={!allChosen} onClick={handleCheck} />
          {lastWrong && (
            <span className="text-sm font-medium text-[#FCA5A5]">Compare the meaning again.</span>
          )}
        </div>
      )}
    </div>
  );
};

export const TimelineBuilderBlock: React.FC<
  {
    block: z.infer<typeof TimelineBuilderBlockSchema>;
    onEvent: OnBlockEvent;
  } & RenderAwareBlockProps
> = ({ block, renderState, blockIndex = 0, onEvent }) => {
  const items = useMemo(() => shuffled(block.items.map((item, id) => ({ ...item, id }))), [block]);
  const [placements, setPlacements] = useState<Map<number, string>>(new Map());
  const [activeItem, setActiveItem] = useState<number | null>(null);
  const [done, setDone] = useState(false);
  const [lastWrong, setLastWrong] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const unplaced = items.filter((item) => !placements.has(item.id));
  const allPlaced = unplaced.length === 0;

  const placeActive = (anchor: string) => {
    if (activeItem === null || done) return;
    setLastWrong(false);
    setPlacements((prev) => {
      const next = new Map(prev).set(activeItem, anchor);
      onEvent({
        event: "working",
        response: items.map((item) => `${item.text} -> ${next.get(item.id) ?? "?"}`).join("; "),
        action: "placed_timeline_item",
        target: block.prompt,
        targetId: composedBlockTargetId(blockIndex, "item", activeItem),
        expected: items.map((item) => `${item.text} -> ${item.anchor}`).join("; "),
        progress: `${next.size}/${items.length} timeline items placed`,
        resolved: false,
      });
      return next;
    });
    setActiveItem(null);
  };

  const unplace = (id: number) => {
    if (done) return;
    setLastWrong(false);
    setPlacements((prev) => {
      const next = new Map(prev);
      next.delete(id);
      onEvent({
        event: "working",
        response: items.map((item) => `${item.text} -> ${next.get(item.id) ?? "?"}`).join("; "),
        action: "removed_timeline_item",
        target: block.prompt,
        targetId: composedBlockTargetId(blockIndex, "item", id),
        expected: items.map((item) => `${item.text} -> ${item.anchor}`).join("; "),
        progress: `${next.size}/${items.length} timeline items placed`,
        resolved: false,
      });
      return next;
    });
  };

  const handleCheck = () => {
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    const wrong = items.filter(
      (item) => normalizeAnswer(placements.get(item.id) ?? "") !== normalizeAnswer(item.anchor)
    );
    const correct = wrong.length === 0;
    setDone(correct);
    setLastWrong(!correct);
    onEvent({
      response: items.map((item) => `${item.text} -> ${placements.get(item.id) ?? "?"}`).join("; "),
      action: "built_timeline",
      target: block.prompt,
      targetId: composedBlockTargetId(blockIndex, "prompt"),
      expected: items.map((item) => `${item.text} -> ${item.anchor}`).join("; "),
      correct,
      resolved: correct,
      attempt: nextAttempt,
    });
  };

  return (
    <div>
      <BlockLabel label={block.label} />
      <WhiteboardWriteInText
        as="p"
        text={block.prompt}
        data-whiteboard-target-id={composedBlockTargetId(blockIndex, "prompt")}
        className={`text-base font-semibold leading-snug text-[#F9F9F9] ${getTutorTargetClass(
          block.prompt,
          renderState,
          composedBlockTargetId(blockIndex, "prompt")
        )}`}
        title={getTutorTargetTitle(
          block.prompt,
          renderState,
          composedBlockTargetId(blockIndex, "prompt")
        )}
      />
      {unplaced.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {unplaced.map((item) => {
            const itemTargetId = composedBlockTargetId(blockIndex, "item", item.id);
            return (
              <button
                key={item.id}
                onClick={() => setActiveItem(activeItem === item.id ? null : item.id)}
                data-whiteboard-target-id={itemTargetId}
                className={`${chipBase} ${
                  activeItem === item.id ? chipActive : chipIdle
                } ${getTutorTargetClass(item.text, renderState, itemTargetId)}`}
                title={getTutorTargetTitle(item.text, renderState, itemTargetId)}
              >
                {item.text}
              </button>
            );
          })}
        </div>
      )}
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {block.anchors.map((anchor) => (
          <button
            key={anchor}
            onClick={() => placeActive(anchor)}
            disabled={activeItem === null || done}
            className={`min-h-[5rem] rounded-xl border p-2 text-left transition-colors ${
              activeItem !== null && !done
                ? "border-[#FD954F]/50 bg-[#FD954F]/5"
                : done
                  ? "border-[#22C55E]/40 bg-[#22C55E]/5"
                  : "border-white/15 bg-white/[0.03]"
            }`}
          >
            <span className="block text-xs font-bold uppercase tracking-wider text-[#FD954F]">
              {anchor}
            </span>
            <span className="mt-1 flex flex-wrap gap-1.5">
              {items
                .filter((item) => placements.get(item.id) === anchor)
                .map((item) => {
                  const itemTargetId = composedBlockTargetId(blockIndex, "item", item.id);
                  return (
                    <span
                      key={item.id}
                      role="button"
                      tabIndex={0}
                      data-whiteboard-target-id={itemTargetId}
                      onClick={(event) => {
                        event.stopPropagation();
                        unplace(item.id);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") unplace(item.id);
                      }}
                      className={`rounded-md border px-2 py-0.5 text-sm font-semibold ${
                        done ? chipCorrect : lastWrong ? chipWrong : chipActive
                      } ${getTutorTargetClass(item.text, renderState, itemTargetId)}`}
                      title={getTutorTargetTitle(item.text, renderState, itemTargetId)}
                    >
                      {item.text}
                    </span>
                  );
                })}
            </span>
          </button>
        ))}
      </div>
      {!done && (
        <div className="mt-3 flex items-center gap-2">
          <CheckButton disabled={!allPlaced} onClick={handleCheck} />
          {lastWrong && (
            <span className="text-sm font-medium text-[#FCA5A5]">
              One item is on the wrong part of the timeline.
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export const DiscourseFunctionBlock: React.FC<
  {
    block: z.infer<typeof DiscourseFunctionBlockSchema>;
    onEvent: OnBlockEvent;
  } & RenderAwareBlockProps
> = ({ block, renderState, blockIndex = 0, onEvent }) => {
  const [choices, setChoices] = useState<Map<number, string>>(new Map());
  const [done, setDone] = useState(false);
  const [lastWrong, setLastWrong] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const allChosen = choices.size === block.phrases.length;

  const choose = (index: number, value: string) => {
    if (done) return;
    setLastWrong(false);
    setChoices((prev) => {
      const next = new Map(prev).set(index, value);
      const functionIndex = block.functions.findIndex((candidate) => candidate === value);
      onEvent({
        event: "working",
        response: block.phrases
          .map((phrase, phraseIndex) => `${phrase.text} -> ${next.get(phraseIndex) ?? "?"}`)
          .join("; "),
        action: "selected_discourse_function",
        target: block.prompt ?? block.label ?? "discourse functions",
        targetId:
          functionIndex >= 0
            ? composedBlockTargetId(blockIndex, "function", index, functionIndex)
            : composedBlockTargetId(blockIndex, "phrase", index),
        expected: block.phrases.map((phrase) => `${phrase.text} -> ${phrase.answer}`).join("; "),
        progress: `${next.size}/${block.phrases.length} functions labeled`,
        resolved: false,
      });
      return next;
    });
  };

  const handleCheck = () => {
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    const correct = block.phrases.every(
      (phrase, index) =>
        normalizeAnswer(choices.get(index) ?? "") === normalizeAnswer(phrase.answer)
    );
    setDone(correct);
    setLastWrong(!correct);
    onEvent({
      response: block.phrases
        .map((phrase, index) => `${phrase.text} -> ${choices.get(index) ?? "?"}`)
        .join("; "),
      action: "labeled_discourse_functions",
      target: block.prompt ?? block.label ?? "discourse functions",
      targetId: composedBlockTargetId(blockIndex, "prompt"),
      expected: block.phrases.map((phrase) => `${phrase.text} -> ${phrase.answer}`).join("; "),
      correct,
      resolved: correct,
      attempt: nextAttempt,
    });
  };

  return (
    <div>
      <BlockLabel label={block.label} />
      {block.prompt && (
        <WhiteboardWriteInText
          as="p"
          text={block.prompt}
          data-whiteboard-target-id={composedBlockTargetId(blockIndex, "prompt")}
          className={`text-base font-semibold leading-snug text-[#F9F9F9] ${getTutorTargetClass(
            block.prompt,
            renderState,
            composedBlockTargetId(blockIndex, "prompt")
          )}`}
          title={getTutorTargetTitle(
            block.prompt,
            renderState,
            composedBlockTargetId(blockIndex, "prompt")
          )}
        />
      )}
      <div className="mt-3 space-y-3">
        {block.phrases.map((phrase, index) => {
          const choice = choices.get(index);
          const phraseTargetId = composedBlockTargetId(blockIndex, "phrase", index);
          return (
            <div key={`${phrase.text}-${index}`} className="rounded-xl border border-white/10 p-3">
              <WhiteboardWriteInText
                as="p"
                text={phrase.text}
                data-whiteboard-target-id={phraseTargetId}
                className={`text-base font-semibold text-[#F9F9F9] ${getTutorTargetClass(
                  phrase.text,
                  renderState,
                  phraseTargetId
                )}`}
                title={getTutorTargetTitle(phrase.text, renderState, phraseTargetId)}
              />
              <div className="mt-2 flex flex-wrap gap-2">
                {block.functions.map((fn, functionIndex) => {
                  const functionTargetId = composedBlockTargetId(
                    blockIndex,
                    "function",
                    index,
                    functionIndex
                  );
                  const picked = choice === fn;
                  const isAnswer = done && normalizeAnswer(fn) === normalizeAnswer(phrase.answer);
                  return (
                    <button
                      key={fn}
                      onClick={() => choose(index, fn)}
                      disabled={done}
                      aria-pressed={picked}
                      data-whiteboard-target-id={functionTargetId}
                      className={`${chipBase} ${
                        isAnswer ? chipCorrect : picked ? chipActive : chipIdle
                      } ${getTutorTargetClass(fn, renderState, functionTargetId)} ${
                        done && picked && !isAnswer ? chipWrong : ""
                      }`}
                      title={getTutorTargetTitle(fn, renderState, functionTargetId)}
                    >
                      {fn}
                    </button>
                  );
                })}
              </div>
              {done && phrase.note && (
                <p className="mt-2 text-sm leading-relaxed text-[#AEAEB2]">{phrase.note}</p>
              )}
            </div>
          );
        })}
      </div>
      {!done && (
        <div className="mt-3 flex items-center gap-2">
          <CheckButton disabled={!allChosen} onClick={handleCheck} />
          {lastWrong && (
            <span className="text-sm font-medium text-[#FCA5A5]">
              One function label does not fit the phrase yet.
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export const TranslationChoiceBlock: React.FC<
  {
    block: z.infer<typeof TranslationChoiceBlockSchema>;
    onEvent: OnBlockEvent;
  } & RenderAwareBlockProps
> = ({ block, renderState, blockIndex = 0, onEvent }) => {
  const [picked, setPicked] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const choose = (option: (typeof block.options)[number]) => {
    if (done) return;
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    setPicked(option.text);
    const correct = normalizeAnswer(option.text) === normalizeAnswer(block.answer);
    const optionIndex = block.options.findIndex((candidate) => candidate.text === option.text);
    setDone(correct);
    onEvent({
      response: option.text,
      action: "selected_translation",
      target: block.source,
      targetId:
        optionIndex >= 0 ? composedBlockTargetId(blockIndex, "option", optionIndex) : undefined,
      expected: block.answer,
      correct,
      resolved: correct,
      attempt: nextAttempt,
    });
  };

  return (
    <div>
      <BlockLabel label={block.label} />
      <div className="rounded-xl border border-white/15 bg-white/[0.04] p-3">
        {block.prompt && (
          <WhiteboardWriteInText
            as="p"
            text={block.prompt}
            className="text-sm font-semibold text-[#FFD9B8]"
          />
        )}
        <WhiteboardWriteInText
          as="p"
          text={block.source}
          data-whiteboard-target-id={composedBlockTargetId(blockIndex, "source")}
          className={`mt-1 text-lg font-semibold text-[#F9F9F9] ${getTutorTargetClass(
            block.source,
            renderState,
            composedBlockTargetId(blockIndex, "source")
          )}`}
          title={getTutorTargetTitle(
            block.source,
            renderState,
            composedBlockTargetId(blockIndex, "source")
          )}
        />
      </div>
      <div className="mt-3 space-y-2">
        {block.options.map((option, index) => {
          const optionTargetId = composedBlockTargetId(blockIndex, "option", index);
          const selected = picked === option.text;
          const isAnswer = done && normalizeAnswer(option.text) === normalizeAnswer(block.answer);
          return (
            <button
              key={option.text}
              onClick={() => choose(option)}
              disabled={done}
              data-whiteboard-target-id={optionTargetId}
              className={`w-full rounded-xl border px-3 py-2 text-left transition-colors ${
                isAnswer ? chipCorrect : selected ? chipActive : chipIdle
              } ${selected && !isAnswer && picked ? chipWrong : ""} ${getTutorTargetClass(
                option.text,
                renderState,
                optionTargetId
              )}`}
              title={getTutorTargetTitle(option.text, renderState, optionTargetId)}
            >
              <span className="block text-base font-semibold text-[#F9F9F9]">{option.text}</span>
              {done && option.note && (
                <span className="mt-1 block text-sm text-[#D1D1D6]">{option.note}</span>
              )}
            </button>
          );
        })}
      </div>
      {picked && !done && (
        <p className="mt-2 text-sm font-medium text-[#FCA5A5]">
          That translation is not the best fit in context. Try the more natural one.
        </p>
      )}
    </div>
  );
};

export const RevealBlock: React.FC<{
  block: z.infer<typeof RevealBlockSchema>;
  onEvent: OnBlockEvent;
}> = ({ block, onEvent }) => {
  const [open, setOpen] = useState(false);
  const reportedRef = useRef(false);

  const handleOpen = () => {
    setOpen(true);
    if (!reportedRef.current) {
      reportedRef.current = true;
      onEvent({
        response: `peeked at "${block.label}"`,
        action: "opened_reveal",
        target: block.label,
      });
    }
  };

  return open ? (
    <div className="rounded-xl border border-[#FD954F]/30 bg-[#FD954F]/10 px-3 py-2 text-base text-[#FFD9B8]">
      <WhiteboardWriteInText text={block.content} />
    </div>
  ) : (
    <button
      onClick={handleOpen}
      className="flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/[0.04] px-3.5 py-2 text-sm font-semibold text-[#AEAEB2] transition-colors hover:border-[#FD954F]/50 hover:text-[#FFD9B8]"
    >
      <Eye size={15} />
      {block.label}
    </button>
  );
};
